<script setup lang="ts">
/**
 * H13b/H14 — Search drawer photo-select (States 6–7)
 * Figma `50:10268` items detected · `50:10269` nothing detected (mobile `50:10487`/`50:10488`)
 * Parts come from `images/get-image-parts`; `detecting` = upload/detect in flight.
 */
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { FairButton } from '@/ui/primitives/FairButton'
import { FairIcon } from '@/ui/primitives/FairIcon'
import { FairIconButton } from '@/ui/primitives/FairIconButton'
import {
  FAIR_HOME_V2_SEARCH_WHOLE_OUTFIT_ID,
  resolveFairHomeV2SearchPhotoFrame,
  type FairHomeV2SearchPhotoPart,
} from './fair-home-v2-search-drawer.data'

const props = withDefaults(
  defineProps<{
    imageUrl: string
    parts?: FairHomeV2SearchPhotoPart[]
    selectedId?: string
    compact?: boolean
    detecting?: boolean
  }>(),
  {
    parts: () => [],
    selectedId: FAIR_HOME_V2_SEARCH_WHOLE_OUTFIT_ID,
    compact: false,
    detecting: false,
  },
)

const emit = defineEmits<{
  back: []
  close: []
  continue: [selectedId: string]
  'update:selectedId': [id: string]
}>()

const { t } = useI18n()

const hasDetected = computed(() => props.parts.length > 0)

const caption = computed(() => {
  if (props.detecting && !hasDetected.value) return t('fair_home_v2.search.photo_detecting')
  return hasDetected.value
    ? t('fair_home_v2.search.photo_found')
    : t('fair_home_v2.search.photo_not_found')
})

const tags = computed(() => {
  const whole = {
    id: FAIR_HOME_V2_SEARCH_WHOLE_OUTFIT_ID,
    label: t('fair_home_v2.search.whole_outfit'),
  }
  if (!hasDetected.value) return [whole]
  return [
    whole,
    ...props.parts.map((part) => ({ id: part.id, label: part.label })),
  ]
})

const frame = computed(() =>
  resolveFairHomeV2SearchPhotoFrame(props.parts, props.selectedId),
)

const frameStyle = computed(() => ({
  left: `${frame.value.x * 100}%`,
  top: `${frame.value.y * 100}%`,
  width: `${frame.value.width * 100}%`,
  height: `${frame.value.height * 100}%`,
}))

function handleSelect(id: string) {
  emit('update:selectedId', id)
}

function handleContinue() {
  emit('continue', props.selectedId)
}
</script>

<template>
  <div
    class="fair-home-v2-search-photo"
    :class="{ 'fair-home-v2-search-photo--compact': compact }"
    :data-state="detecting && !hasDetected ? 'detecting' : hasDetected ? 'detected' : 'none'"
  >
    <header class="fair-home-v2-search-photo__header">
      <FairIconButton
        icon="arrow-left"
        :size="compact ? 'mobile' : 'medium'"
        :aria-label="t('fair_home_v2.search.photo_back')"
        @click="emit('back')"
      />
      <h2 class="fair-home-v2-search-photo__title">
        {{ t('fair_home_v2.search.photo_select_title') }}
      </h2>
      <FairIconButton
        icon="close"
        :size="compact ? 'mobile' : 'medium'"
        :aria-label="t('fair_home_v2.search.close')"
        @click="emit('close')"
      />
    </header>
    <div
      class="fair-home-v2-search-photo__rule"
      aria-hidden="true"
    />

    <div class="fair-home-v2-search-photo__body">
      <div class="fair-home-v2-search-photo__caption">
        <FairIcon
          name="ai"
          :size="12"
          class="fair-home-v2-search-photo__caption-icon"
        />
        <p class="fair-home-v2-search-photo__caption-text">
          {{ caption }}
        </p>
      </div>

      <div
        class="fair-home-v2-search-photo__tags"
        role="listbox"
        :aria-label="t('fair_home_v2.search.photo_parts')"
      >
        <button
          v-for="tag in tags"
          :key="tag.id"
          type="button"
          class="fair-home-v2-search-photo__tag"
          :class="{
            'fair-home-v2-search-photo__tag--on': tag.id === selectedId,
          }"
          role="option"
          :aria-selected="tag.id === selectedId"
          @click="handleSelect(tag.id)"
        >
          {{ tag.label }}
        </button>
      </div>

      <div class="fair-home-v2-search-photo__stage">
        <img
          class="fair-home-v2-search-photo__image"
          :src="imageUrl"
          alt=""
        >
        <div
          class="fair-home-v2-search-photo__frame"
          :style="frameStyle"
          aria-hidden="true"
        >
          <span class="fair-home-v2-search-photo__grabber fair-home-v2-search-photo__grabber--nw" />
          <span class="fair-home-v2-search-photo__grabber fair-home-v2-search-photo__grabber--ne" />
          <span class="fair-home-v2-search-photo__grabber fair-home-v2-search-photo__grabber--se" />
          <span class="fair-home-v2-search-photo__grabber fair-home-v2-search-photo__grabber--sw" />
        </div>
      </div>
    </div>

    <div
      class="fair-home-v2-search-photo__rule"
      aria-hidden="true"
    />
    <footer class="fair-home-v2-search-photo__footer">
      <FairButton
        class="fair-home-v2-search-photo__continue"
        variant="primary"
        size="m"
        :rounded="false"
        :label="t('fair_home_v2.search.photo_continue')"
        @click="handleContinue"
      />
    </footer>
  </div>
</template>
